/**
 * First-party connectors — integrations we vouch for and surface as one-tap connectors
 * (vs the user-pasted "custom MCP server" flow). This registry provides the curated
 * identity (name/logo/address-or-slug) so product surfaces (web Connectors screen,
 * harness connectors.md) can offer them without asking the user for an address.
 *
 * Entries differ in HOW the connection is created, which is the `kind` below. Three of the
 * four end up as an `McpConnection` and are, once connected, indistinguishable from an
 * external MCP server to everything upstream (mcp.md, McpFindTools, the connector row).
 * `wix-service` never does, so any surface that enumerates connections must filter on
 * `isMcpBackedConnector` and handle it separately. Read `FirstPartyConnectorKind` before
 * adding a surface that iterates this list.
 */

/**
 * Which rail creates the connection.
 *
 * `mcp-server`: the vendor runs an MCP server. We hold its canonical URL and connect by
 * URL, letting the service probe it and run the MCP-spec OAuth dance (discovery, dynamic
 * client registration, PKCE). WordPress.com.
 *
 * `registered-connector`: the vendor runs an ordinary HTTP API and no MCP server, so Wix
 * registered an OAuth app up front in the vendor's developer console and the service
 * connects by SLUG, using that connector's own OAuth dialect. There is deliberately no
 * URL on these entries: the address the assistant ends up dialing is our own connector
 * gateway, derived server-side from the slug — putting it here would be a second source
 * of truth, and `CreateConnection` refuses that route by design. TikTok.
 *
 * `partner-install`: nothing on our side creates the connection at all. A SIBLING WIX
 * PRODUCT already holds the merchant's authorization, and the connection appears once the
 * user installs that product's app in the vendor's own marketplace. Shopify, via Rise.
 * So there is no URL, no slug to authorize, and no authorize link to hand back — the only
 * thing we can give the user is `installUrl`, and every "connect it with ManageMcp" line
 * on the other two rails is a lie on this one. Callers must branch on the kind rather than
 * treating "not an mcp-server" as "connectable by name".
 *
 * `wix-service`: the connect flow is owned by ANOTHER WIX SERVICE, which holds the vendor
 * credential and refreshes it. We only start the flow and read the status. Google Business
 * Profile, via `wix.gbp.connection.v1.GbpConnectionService`.
 * Alone among the four it produces NO `McpConnection` at all, so it has no connection id, no
 * tools snapshot, no granted scopes, and no `status` of the shape the others share. Filter it
 * out with `isMcpBackedConnector` rather than testing `kind` inline, so a fifth rail is one
 * edit here instead of a hunt through five packages.
 *
 * `direct-oauth`: our own vendor app, with an OAuth endpoint per merchant account (Shopify's is per store),
 * so the user names the account first and the connect is a link to our server's start endpoint, which runs
 * the vendor's OAuth in the browser and writes the McpConnection itself. Shopify, on our own app
 * (`shopify-direct`), replacing the Rise partner install.
 */

/** A vendor-run MCP server: connected by its canonical URL. */

/** A vendor app Wix registered up front: connected by slug, with no URL of its own. */

/**
 * A connector whose authorization belongs to a sibling Wix product, reached by installing that
 * product's app in the vendor's own marketplace.
 */

/** Our own vendor app whose OAuth runs per merchant account: the user names the account, then follows `startUrl`. */

/**
 * A connect flow owned by another Wix service. No URL and no slug to dial: the owning service is
 * resolved in code, because unlike the other rails there is nothing per-entry to configure. One
 * `wix-service` entry means one hand-written client, so a data field naming the service would be a
 * second source of truth for something already pinned by an import.
 */

/** Backed by an `McpConnection` row, so connection-shaped code (rows, tools, statuses) applies. */

/**
 * True when this entry's state lives in an `McpConnection`.
 *
 * The guard every connection-enumerating surface filters on. Note `partner-install` IS backed by one:
 * we do not create it, but the partner's install does, so it pairs to a connection like any other. Only
 * `wix-service` has none, and matching it against a connection list yields nothing, which renders it as
 * permanently "Connect" and offers a second connection over one the user already has.
 */
export function isMcpBackedConnector(connector) {
  return connector.kind !== 'wix-service';
}
export const WORDPRESS_MCP_SERVER_URL = 'https://public-api.wordpress.com/wpcom/v2/mcp/v1';
export const WORDPRESS_MCP_CONNECTOR = {
  kind: 'mcp-server',
  slug: 'wordpress',
  name: 'WordPress',
  url: WORDPRESS_MCP_SERVER_URL,
  logoUrl: 'https://logos.composio.dev/api/wordpress',
  description: 'Manage your WordPress.com site — posts, pages, media, comments, and stats.',
  // The switches belong to the ACCOUNT rather than to a site's Settings menu, and the authorize completes
  // whatever their state (VMR #28855), so a CONNECTED row proves nothing about whether a tool will run.
  //
  // Every alternative below is a SITE, and the plan belongs to the site rather than to the account that
  // holds it — an account can carry one paid site and one lapsed free one — so "that site's account" is
  // what resolves on all three paths.
  //
  // The three sit inside a dash-bracketed aside, behind a head that states the condition generally, because
  // whichever one leads is the one that gets answered from. As a bare list starting with the paid plan, it
  // had a free-site owner told to upgrade, and the 30-day path was in the text the whole time.
  //
  // It states what a qualifying site ALREADY has and never names a product to buy. WordPress is a
  // deprioritized connector (see `DEPRIORITIZED_CONNECTOR_NORMS`): connectable on request, never
  // suggested. The self-hosted route runs on Jetpack, which is the competitor's own paid add-on, so
  // naming its tiers here would have Symphony selling them — the eligibility gate is ours to state, the
  // upsell is not ours to make. A site that already has it recognises itself in this wording.
  //
  // WordPress.com documents this in two places that disagree, both read 2026-09-09:
  // developer.wordpress.com/docs/mcp carries the URL below, the one the server prints in its own refusals,
  // and says the master switch enables reads only; wordpress.com/support/mcp names the Read and Write tool
  // groups, the site exceptions and the Jetpack plans, and says the master switch enables both groups. So
  // the groups are named and no default is claimed for either.
  //
  // ONE noun clause, because five surfaces splice it into "Needs …", "It needs …" and "check with them that
  // they have …", two of them on paths carrying no authorize link. A sentence of our own in here reaches
  // those as an order the assistant cannot follow, so what to DO with it lives on the authorize return.
  prerequisite: "a site WordPress.com will serve MCP for — a WordPress.com site on a PAID plan, a free WordPress.com site still inside its first 30 days, or a self-hosted site whose existing Jetpack plan already carries MCP access — with MCP switched on for that site's WordPress.com account at https://wordpress.com/me/mcp, the Read and Write tool groups switched on there, and that site absent from the same page's site exceptions",
  connectPrompt: 'I want to connect WordPress (WordPress.com).',
  // The vendor-qualified spellings, which is how users and `connectPrompt` above actually say it. Without
  // these the connect refuses the very name the product puts in the user's mouth.
  aliases: ['wordpress.com', 'wordpress.org']
};

/**
 * Plaud's hosted MCP server — the FIRST wearable on any Symphony rail.
 *
 * The canonical URL is not copied from a blog post: the server states it itself. Its
 * `/.well-known/oauth-protected-resource` answers `{"resource":"https://mcp.plaud.ai/mcp", ...}`, and
 * `/.well-known/oauth-authorization-server` publishes `registration_endpoint` alongside PKCE `S256` —
 * precisely the discovery + dynamic-client-registration + PKCE dance this rail runs, so it needs no
 * pre-registered client from us. Verified 2026-09-03.
 *
 * ⚠️ A plain GET of the resource URL answers **404**, and that is not a dead endpoint: a Streamable-HTTP
 * MCP server rejects a bare GET. Probe the two `.well-known` documents above before ever concluding this
 * address is wrong — the 404 alone nearly kept this entry from being written.
 *
 * Why a wearable earns a first-party row when no other one does: of every ambient-AI and health device
 * checked (Limitless, Bee, Omi, Oura, Whoop, Fitbit, Garmin, Apple, Samsung, Meta, Rabbit), Plaud is the
 * only vendor running a hosted, OAuth'd MCP server. NONE of them is in the Composio catalog, so that rail
 * cannot serve this category at all and `ConnectApp` must never be reached for one. Limitless is the sole
 * near-miss — its MCP server is real but takes a bare `X-API-Key` rather than OAuth, and Meta
 * discontinued the Pendant in Dec 2025, so it is deliberately absent.
 */
export const PLAUD_MCP_SERVER_URL = 'https://mcp.plaud.ai/mcp';

/**
 * The asset behind this path is NOT uploaded yet — the same position as `GBP_LOGO_URL`, and safe for the
 * same reason: the entry rides its own default-off rollout, so nobody sees the card until that rollout
 * opens. Uploading the mark is a blocker for OPENING the rollout, not for merging this.
 *
 * Deliberately not `logos.composio.dev/api/plaud`: that host answers 200 for any slug, and since Plaud is
 * in no Composio catalog what it serves is the generic grey placeholder rather than Plaud's mark. A
 * placeholder that loads is worse than a URL that fails, because the letter fallback never gets its turn.
 */
export const PLAUD_LOGO_URL = 'https://static.wixstatic.com/media/symphony-connectors/plaud.svg';
export const PLAUD_ROLLOUT_KEY = 'plaud';
export const PLAUD_CONNECTOR = {
  kind: 'mcp-server',
  slug: 'plaud',
  name: 'Plaud',
  url: PLAUD_MCP_SERVER_URL,
  logoUrl: PLAUD_LOGO_URL,
  // What the device actually holds, in the user's own terms: it is a recorder, so the value is the
  // meetings and notes it captured — never "wearable data".
  description: 'Read your Plaud recorder’s meetings, transcripts and notes, so they can be searched, summarised and followed up.',
  prerequisite: 'a Plaud account with recordings already synced from the device — the server reads what is in their Plaud account, so a recorder that has never synced authorizes fine and comes back with nothing to read',
  connectPrompt: 'I want to connect Plaud.',
  // How people actually name it: the hardware, not the company. This row is behind its own rollout and
  // never suggested, so the NAME is the only handle a user ever arrives with, and a spelling that does not
  // resolve is a spelling that cannot be connected.
  aliases: ['plaud.ai', 'plaud note', 'plaud notepin', 'notepin'],
  rolloutKey: PLAUD_ROLLOUT_KEY
};
export const TIKTOK_CONNECTOR = {
  kind: 'registered-connector',
  slug: 'tiktok',
  name: 'TikTok',
  logoUrl: 'https://logos.composio.dev/api/tiktok',
  // Says "drafts" because that is the whole product promise here: we push the video into TikTok and the
  // user finishes it there. Claiming we post would be wrong, and it is the difference that decides
  // whether TikTok's Content Sharing Guidelines fall on us.
  description: 'Send videos to your TikTok drafts and read your account. You add the caption and post from TikTok.',
  prerequisite: 'a TikTok account they can log into, and the video already in Symphony (an attachment they sent or a file in their workspace) — TikTok fetches it from Wix itself, so a video hosted anywhere else cannot be sent',
  connectPrompt: 'I want to connect TikTok.',
  gated: true
};

/**
 * Rise's listing on the Shopify App Store. Rise is Wix's own gift-card and loyalty app for Shopify, and
 * it — not Symphony — holds the merchant's Shopify authorization.
 *
 * `utm_source=symphony` is NOT analytics decoration. Rise validates it to decide whether an install is
 * Symphony-attributed, and only an attributed install puts the Symphony app on the account and tells us
 * about it. Send a user to the bare listing and they get a working Rise install with no Symphony
 * connection and no error to explain why.
 *
 * This is the CANONICAL spelling, and it is not what any user is handed — `attributedInstallUrl` stamps
 * the caller's metasite into that same parameter first. Keep this one unstamped: it is what the registry
 * is matched on.
 */
export const RISE_SHOPIFY_APP_STORE_URL = 'https://apps.shopify.com/gift-card-loyalty-program?utm_source=symphony';

/**
 * The query parameter that carries our attribution through a partner's marketplace.
 *
 * `utm_source` rather than a parameter of our own choosing because it is the only one that SURVIVES the
 * trip: the user clicks our link, the Shopify App Store runs its own install flow, and Rise's install
 * callback receives the utm parameters and nothing else we could have put on the URL.
 */
const ATTRIBUTION_PARAM = 'utm_source';

/** Wix ids are GUIDs, and this value is concatenated into a string the partner parses. */
const GUID_PATTERN = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';
const GUID = new RegExp(`^${GUID_PATTERN}$`, 'i');

/**
 * The install link for THIS caller: the canonical listing with a server-minted handoff token folded into
 * the attribution parameter, as `symphony-{attributionToken}`.
 *
 * This is the only thing that ties the two sides together, and it exists because they have no id in
 * common. A Rise account is its OWN metasite under Rise's tenancy, created per store at install time; our
 * user is a siteless metasite that Rise has never heard of. So the link the user clicks carries the token
 * outward, Rise keeps it against the installation, and it comes back on the install event next to Rise's
 * own account id — which is the pair we need to fetch credentials at all. Nothing else in the flow
 * crosses that boundary: the event's other identifiers are all Rise's.
 *
 * **It used to be the metasite here, and that is why nothing ever connected.** This parameter travels
 * through a public marketplace, so a metasite in it named an account the install report could not prove.
 * The missing proof was made up by asking the merchant to name their own shop, and they could not — the
 * handle is Shopify-generated and nobody knows it from memory. A token is unguessable and bound
 * server-side to one metasite AND one Wix account, so the report proves itself and the merchant is asked
 * for nothing. Treat it as a credential: it belongs in this link and nowhere else.
 *
 * Still shaped as a canonical GUID, which is load-bearing rather than cosmetic — Rise propagates the
 * suffix only when it matches a lower-case GUID, and drops anything else silently.
 *
 * Undefined when there is no token to stamp, and deliberately NOT "fall back to the bare link". An
 * unstamped install still succeeds, still reads as Symphony-attributed, and still puts the Symphony app on
 * the account — it just arrives with nothing to match it against. The user would do every step correctly
 * and end up with no connection and no error, which is the one outcome worth refusing before it starts.
 */
export function attributedInstallUrl(installUrl, attributionToken) {
  const msid = attributionToken == null ? void 0 : attributionToken.trim();
  if (!msid || !GUID.test(msid)) {
    return undefined;
  }
  let url;
  try {
    url = new URL(installUrl);
  } catch {
    return undefined;
  }
  const attribution = url.searchParams.get(ATTRIBUTION_PARAM);
  // No attribution slot means no way to carry the id, so there is nothing to hand over — same refusal as
  // a missing metasite rather than a link that cannot complete.
  if (!attribution) {
    return undefined;
  }
  url.searchParams.set(ATTRIBUTION_PARAM, `${attribution}-${msid}`);
  return url.toString();
}

/**
 * Shopify's own rollout key, paired with the `shopify-connector-rollout` databag value. Exported so the
 * surfaces that supply the verdict key off the same literal the entry declares
 * — a hand-typed second copy is how an entry ends up permanently dark with a rollout that looks open.
 */
export const SHOPIFY_ROLLOUT_KEY = 'shopify';

/**
 * Shopify, reached through Rise.
 *
 * On this rail we never run an OAuth flow and never ask Shopify for anything on our own account: Rise
 * owns the merchant's authorization, and Symphony's access is a consequence of the merchant installing
 * Rise from the link above. That is why this is a `partner-install` and not the `registered-connector`
 * the McpConnectionService protos anticipated — there is no Symphony-side authorize step to run.
 *
 * GATED on its OWN rollout (`shopify-connector-rollout`), not the shared connector one, so Shopify can be
 * controlled independently of TikTok and the other first-party connectors. `true` opens Shopify to
 * everyone; any other non-empty value is a Wix user-ID allowlist.
 *
 * No longer in `DEPRIORITIZED_CONNECTOR_NORMS`: the terms question closed, so it may be pitched. It is
 * still independently controlled by its own rollout key. The production-wide state is the ordinary
 * `shopify-connector-rollout=true` configuration.
 */
export const SHOPIFY_CONNECTOR = {
  kind: 'partner-install',
  slug: 'shopify',
  name: 'Shopify',
  installUrl: RISE_SHOPIFY_APP_STORE_URL,
  // Shopify's own word, and what the user will answer with: `acme.myshopify.com`.
  accountLabel: 'store',
  logoUrl: 'https://logos.composio.dev/api/shopify',
  description: 'Reach your Shopify store from Symphony, through Rise — the Wix gift-card and loyalty app for Shopify.',
  // Says the install must start from the link Symphony hands over, because that is the failure the
  // assistant would otherwise cause and never detect: a user who installs Rise from a search result has
  // done everything asked of them and still has no connection.
  //
  // Says the plan step too. Rise asks a live store to choose one of its plans before it will finish
  // setting the store up, and a merchant who is not told that hits a payment screen they did not expect
  // in the middle of connecting something else — reading as Symphony having sent them somewhere to be
  // charged. It is invisible in testing because a Shopify DEVELOPMENT store is exempt, which is the only
  // kind either team has run this on.
  //
  // Deliberately does not say "the link on this row" — this string is read on the connectors.md row AND
  // interpolated into the connect tool's return, which has no row, and the row no longer carries the link.
  prerequisite: "a Shopify store they own, with the Rise app installed on it from the install link Symphony gives them — Symphony reaches Shopify only through Rise, and an install that didn't start from that link connects Rise without connecting Symphony. A live store also has to choose a Rise plan, which Rise asks for during setup and which is a paid Rise subscription, separate from anything the user pays Wix for Symphony",
  connectPrompt: 'I want to connect Shopify.',
  aliases: ['shopify.com'],
  rolloutKey: SHOPIFY_ROLLOUT_KEY
};

/** Paired with the `shopify-direct-connector-rollout` databag value (empty = off, `true`, or metasite ids). */
export const SHOPIFY_DIRECT_ROLLOUT_KEY = 'shopify-direct';

/**
 * The server's browser start endpoint for the direct connect. Site and user come from the Wix session, never the query.
 * On the server's `_serverless` base, a prefix mapping: the `www.wix.com/_api` base is mapped per exact path, and the
 * server scopes the attempt cookie to `/_serverless/ai-scheduling-assistant-server/shopify-direct`.
 */
export const SHOPIFY_DIRECT_START_URL = 'https://manage.wix.com/_serverless/ai-scheduling-assistant-server/shopify-direct/start';

/**
 * Shopify on our own app: direct OAuth per store, Symphony holds the token. Replaces the Rise partner install
 * (`SHOPIFY_CONNECTOR`), and runs beside it behind its own rollout during the move. Same tools under the slug
 * `shopify-direct`, so a site runs at most ONE of the two — see `shopifyRolloutVerdicts`, and
 * `shopifyConnectionInUse` for which one runs when a switch site holds both.
 *
 * Listed after the Rise entry on purpose: a lookup over the whole registry by the name "Shopify" answers Rise.
 * Narrowed lists hold at most one of the two, except on a switch site, where the direct entry is reached by its
 * slug and its own `connectPrompt`.
 */
export const SHOPIFY_DIRECT_CONNECTOR = {
  kind: 'direct-oauth',
  slug: 'shopify-direct',
  name: 'Shopify',
  startUrl: SHOPIFY_DIRECT_START_URL,
  accountLabel: 'store',
  logoUrl: 'https://logos.composio.dev/api/shopify',
  description: 'Reach your Shopify store from Symphony: products, stock, orders and pages, with your approval before any change.',
  prerequisite: 'access to a Shopify store they own or manage',
  // Sites moving off Rise keep knowledge saying Shopify needs the Rise app and a paid Rise plan, and the agent
  // repeated it over this row.
  assistantNote: 'This Shopify connection works without Rise: it needs no Rise app and no Rise plan, so anything saying Shopify needs Rise is out of date. Do not bring up Rise yourself. A user who says shopify-direct or direct Shopify means this one.',
  // Not "Shopify": a switch site lists Rise too, and that name resolves to Rise first.
  connectPrompt: 'I want to connect direct Shopify.',
  aliases: ['direct shopify'],
  rolloutKey: SHOPIFY_DIRECT_ROLLOUT_KEY
};

/** What the Shopify rules read off a site's connection: which connector it is, whether it works, and when it was made. */

/** A row with no status is counted as working, so callers that never read one keep the age rule alone. */
const isWorkingShopifyConnection = connection => !connection.status || connection.status === 'CONNECTED';
const shopifySlugOf = connection => {
  var _connection$connector;
  return (_connection$connector = connection.connectorSlug) == null ? void 0 : _connection$connector.trim().toLowerCase();
};
function earliestCreatedMs(connections) {
  const times = connections.map(connection => connection.createdDate == null ? NaN : new Date(connection.createdDate).getTime()).filter(time => Number.isFinite(time));
  return times.length > 0 ? Math.min(...times) : undefined;
}

/**
 * Which of a site's Shopify connections is the one in use. The first connection wins (product decision,
 * 2026-10-15): with one of the two, that one; with both, the older; with neither, none. When the two ages
 * cannot be compared, Rise wins, which is what those sites had before the direct connector existed.
 * A working connection beats one that is not (an uninstall leaves the direct row behind, marked unusable),
 * so a dead row never switches off the live one.
 */
export function shopifyConnectionInUse(connections) {
  const rise = connections.filter(connection => shopifySlugOf(connection) === SHOPIFY_CONNECTOR.slug);
  const direct = connections.filter(connection => shopifySlugOf(connection) === SHOPIFY_DIRECT_CONNECTOR.slug);
  if (direct.length === 0) {
    return rise.length > 0 ? SHOPIFY_CONNECTOR.slug : undefined;
  }
  if (rise.length === 0) {
    return SHOPIFY_DIRECT_CONNECTOR.slug;
  }
  const riseWorks = rise.some(isWorkingShopifyConnection);
  if (riseWorks !== direct.some(isWorkingShopifyConnection)) {
    return riseWorks ? SHOPIFY_CONNECTOR.slug : SHOPIFY_DIRECT_CONNECTOR.slug;
  }
  const riseMs = earliestCreatedMs(rise);
  const directMs = earliestCreatedMs(direct);
  return riseMs !== undefined && directMs !== undefined && directMs < riseMs ? SHOPIFY_DIRECT_CONNECTOR.slug : SHOPIFY_CONNECTOR.slug;
}

/**
 * True for the Shopify connection that lost to the site's other one. It stays listed so the user can
 * disconnect it, and nothing else on it runs: the site has one Shopify.
 */
export function isSupersededShopifyConnection(connection, connections) {
  const slug = shopifySlugOf(connection);
  if (slug !== SHOPIFY_CONNECTOR.slug && slug !== SHOPIFY_DIRECT_CONNECTOR.slug) {
    return false;
  }
  const inUse = shopifyConnectionInUse(connections);
  return inUse !== undefined && inUse !== slug;
}

/**
 * The two Shopify verdicts for one site, so only one Shopify connector is offered there (both on a switch site).
 *
 * - The site's connections decide first (`shopifyConnectionInUse`): a Rise connection that is in use keeps
 *   Rise, and a direct one that is in use keeps the direct connector, even after Rise opens for the user.
 * - A site with neither is offered the direct connector while the direct rollout is open, else Rise.
 * - A site off the direct rollout gets exactly the Rise verdict it had before the direct connector existed.
 * - A debug site (`switchSite`) is offered every Shopify connector open to it, whatever it holds, so the team
 *   can disconnect one and connect the other. Which connection runs still follows `shopifyConnectionInUse`.
 */
export function shopifyRolloutVerdicts(input) {
  if (input.switchSite) {
    return {
      [SHOPIFY_ROLLOUT_KEY]: input.riseOpen,
      [SHOPIFY_DIRECT_ROLLOUT_KEY]: input.directOpen
    };
  }
  const direct = input.directOpen && shopifyConnectionInUse(input.connections) !== SHOPIFY_CONNECTOR.slug;
  return {
    [SHOPIFY_ROLLOUT_KEY]: input.riseOpen && !direct,
    [SHOPIFY_DIRECT_ROLLOUT_KEY]: direct
  };
}

/**
 * Google Business Profile's mark, which we have to host ourselves.
 *
 * `logos.composio.dev` has no GBP logo and does not 404 for one: every unrecognized slug returns the same
 * 2319-byte placeholder SVG with a 200. So pointing there would paint a generic glyph on all three
 * clients, and the clients' own fallback (the connector's initial letter, see `ConnectorLogoCache`) never
 * fires because the fetch technically succeeded. A wrong mark that loads is worse than one that doesn't.
 *
 * So the mark ships as `orion-web/public/google-business-profile.svg`, which that app serves at this
 * path. Hosting it in the repo rather than a media library keeps the asset versioned with the entry that
 * names it, and reaches web, iOS and Android over one ordinary public URL.
 *
 * The domain is spelled out rather than reusing `SYMPHONY_WEB_BASE_URL` from the barrel: the barrel
 * re-exports THIS file, so importing from it here is a cycle.
 */
export const GBP_LOGO_URL = 'https://symphony.wix.com/google-business-profile.svg';
export const GBP_ROLLOUT_KEY = 'gbp';
export const GBP_CONNECTOR = {
  kind: 'wix-service',
  slug: 'gbp',
  skill: 'using-google-business-profile',
  name: 'Google Business Profile',
  logoUrl: GBP_LOGO_URL,
  description: 'Read and reply to your Google reviews, and see how customers find you on Search and Maps.',
  // States what must ALREADY be true for the connect to be useful, and stops there. An earlier version
  // added "Symphony cannot claim or verify a listing", which is false: the platform has RPCs for both. A
  // prerequisite is the wrong place to make claims about our own capabilities anyway, because it is read
  // out when a connect FAILS, so a wrong one sends the user away from something we could do.
  prerequisite: 'a Google account that already manages this business on Google Business Profile. Connecting an account with no listing on it will succeed and then have nothing to show',
  connectPrompt: 'I want to connect Google Business Profile.',
  // Nobody says "GBP" to an assistant, and almost nobody says the current official name. "Google My
  // Business" is the old brand and still the common one; the review-flavoured spellings are what a user
  // actually types when they want this. A gated connector is connected BY NAME (it is on no list to copy
  // a slug from), so an unresolvable spelling is an unconnectable connector.
  aliases: ['google business profile', 'google my business', 'gmb', 'google reviews', 'google business'],
  rolloutKey: GBP_ROLLOUT_KEY
};

/**
 * Paired with the `gsc-mcp-rollout` databag value — the SAME key the harness reads to list the `gsc` MCP server
 * (WIXSEO-5484), so the tile and the agent's tools open together. A rollout is TWO applies with the same value:
 * orion-harness-poc and ai-scheduling-assistant-server.
 */
export const GSC_ROLLOUT_KEY = 'gsc';

/**
 * Google Search Console's mark, hosted like GBP's and Alfred's (`orion-web/public/google-search-console.svg`).
 *
 * `logos.composio.dev/api/google_search_console` does serve a real mark, but this entry exists to retire the
 * Composio GSC toolkit, so the tile must not depend on Composio's CDN keeping that asset.
 */
export const GSC_LOGO_URL = 'https://symphony.wix.com/google-search-console.svg';

/**
 * Google Search Console, via the SEO team's `wix.gsc.connection.v1.GscConnectionService`.
 *
 * Replaces the Composio GSC toolkit: its name normalises to `googlesearchconsole`, the same key the Composio
 * toolkit's slug and name normalise to, so every spelling of it is blocked on the Composio rail for every
 * account. Accounts that still hold a Composio GSC connection are shown this row with a plain Connect, and
 * the leftover Composio account is deleted once they connect (server `services/gscConnector.ts`).
 */
export const GSC_CONNECTOR = {
  kind: 'wix-service',
  slug: 'gsc',
  skill: 'using-google-search-console',
  name: 'Google Search Console',
  logoUrl: GSC_LOGO_URL,
  description: 'See how your site performs in Google Search, submit sitemaps, and check whether pages are indexed.',
  // Any Google account can authorize; the site itself is verified and added afterwards through the readiness
  // tools, so an account with no Search Console property on it still connects and then has nothing to show.
  prerequisite: 'a Google account that manages the site in Search Console, or one they can verify it with. Connecting an account with no Search Console property on it will succeed and then have nothing to show until the site is verified',
  connectPrompt: 'I want to connect Google Search Console.',
  aliases: ['search console', 'gsc', 'google webmaster tools', 'webmaster tools'],
  rolloutKey: GSC_ROLLOUT_KEY
};

/**
 * Paired with the `social-publisher-connector-rollout` databag value, which the harness reads for the same entry's
 * connect rail, tools and skill, so the row and the agent open together. A rollout is TWO applies with the same
 * value: orion-harness-poc and ai-scheduling-assistant-server.
 */
export const SOCIAL_FACEBOOK_ROLLOUT_KEY = 'social-facebook';

/** Facebook's mark, hosted like GSC's (`orion-web/public/facebook.svg`) rather than on the Composio CDN it replaces. */
export const SOCIAL_FACEBOOK_LOGO_URL = 'https://symphony.wix.com/facebook.svg';

/**
 * Facebook Pages, through the Promote team's social-publisher (`com.wixpress.promote.social-publisher`) — the same
 * Facebook connection the Wix dashboard's Social Marketing uses.
 *
 * Unlike GBP and GSC, the connection is not the Symphony account's own: it lives on the metasite the user binds at
 * connect time (a linked Wix site, reusing a Facebook connection already made in the dashboard, or the Symphony
 * metasite when the business has no Wix site). The binding is kept in the account's brain/ preferences file
 * (common-lib `SymphonyFileSocialBindingStore`, a POC stand-in).
 *
 * Piloted behind an allowlist, so the Composio Facebook toolkit stays live (`liveComposioToolkit`): retired only for
 * a caller whose rollout shows this row, and kept for everyone else.
 */
export const SOCIAL_FACEBOOK_CONNECTOR = {
  kind: 'wix-service',
  slug: 'social-facebook',
  skill: 'using-social-publishing',
  name: 'Facebook - Organic marketing',
  logoUrl: SOCIAL_FACEBOOK_LOGO_URL,
  description: 'Publish posts to your Facebook Page through Wix, after you approve each one.',
  // The Graph API publishes to Pages only; a personal profile has nothing to post to.
  prerequisite: 'a Facebook account that manages the business’s Facebook Page. Posts go to a Page, never to a personal profile',
  connectPrompt: 'I want to connect Facebook - Organic marketing.',
  // Never plain "facebook": Composio's Facebook tools are named `FACEBOOK_*`, and ComposioExecuteTool refuses a tool
  // whose prefix resolves to a registry entry, so that alias would switch Composio Facebook off for EVERY account,
  // rollout or not. Plain "facebook" stays Composio's; this entry answers to its own spellings.
  // "facebook - social share" is the name it had before "Facebook - Organic marketing".
  aliases: ['facebook - organic marketing', 'facebook organic marketing', 'facebook - social share', 'facebook social share', 'facebook (wix)', 'facebook wix', 'wix facebook', 'facebook page', 'facebook pages'],
  rolloutKey: SOCIAL_FACEBOOK_ROLLOUT_KEY,
  liveComposioToolkit: 'facebook'
};

/**
 * Paired with the `bing-webmaster-mcp-rollout` databag value: the SAME key the harness reads to list the
 * `bing-webmaster` MCP server (design log 012 slice 8), so the tile and the agent's tools open together. A rollout
 * is TWO applies with the same value: orion-harness-poc and ai-scheduling-assistant-server.
 */
export const BING_WEBMASTER_ROLLOUT_KEY = 'bing-webmaster';

/**
 * Bing Webmaster Tools' mark, hosted like GSC's (`orion-web/public/bing-webmaster-tools.svg`): `logos.composio.dev`
 * has no Bing Webmaster Tools toolkit to borrow one from, and answers an unknown slug with its placeholder and a 200.
 */
export const BING_WEBMASTER_LOGO_URL = 'https://symphony.wix.com/bing-webmaster-tools.svg';

/**
 * Bing Webmaster Tools, via the SEO team's `wix.bwt.connection.v1.BwtConnectionService` (design log 012 slice 9).
 * GSC's twin, without its Composio past: there is no Composio Bing Webmaster toolkit to retire or migrate from.
 * Its slug is the harness's first-party server slug, so the MCP rail and ManageMcp resolve to this one entry.
 * "webmaster tools" is left to GSC's entry, which is registered first and already answers to it.
 */
export const BING_WEBMASTER_CONNECTOR = {
  kind: 'wix-service',
  slug: 'bing-webmaster',
  skill: 'using-bing-webmaster-tools',
  name: 'Bing Webmaster Tools',
  logoUrl: BING_WEBMASTER_LOGO_URL,
  description: 'Add your site to Bing, verify it, and submit its sitemap so Bing can find your pages.',
  // Any Microsoft account can authorize; the site itself is added and verified afterwards through the readiness
  // tools, so an account with no site on it still connects and then has nothing to show.
  prerequisite: 'a Microsoft account that manages the site in Bing Webmaster Tools, or one they can verify it with. Connecting an account with no site on it will succeed and then have nothing to show until the site is added and verified',
  connectPrompt: 'I want to connect Bing Webmaster Tools.',
  aliases: ['bing', 'bing webmaster'],
  rolloutKey: BING_WEBMASTER_ROLLOUT_KEY
};

/**
 * Alfred's mark, hosted the same way as GBP's above and for the same reason: `logos.composio.dev` answers
 * every unknown slug with the same placeholder and a 200.
 */
export const ALFRED_LOGO_URL = 'https://symphony.wix.com/alfred.svg';
export const ALFRED_ROLLOUT_KEY = 'alfred';
export const ALFRED_CONNECTOR = {
  kind: 'wix-service',
  slug: 'alfred',
  skill: 'using-alfred',
  name: 'Alfred',
  logoUrl: ALFRED_LOGO_URL,
  description: 'Answer your business calls with an AI receptionist that answers callers from your business knowledge.',
  connectPrompt: 'I want to connect Alfred.',
  aliases: ['phone receptionist', 'ai receptionist', 'voice receptionist'],
  rolloutKey: ALFRED_ROLLOUT_KEY
  // Not hidden here: each client leaves Alfred off its Tools list exactly when it draws Alfred's Connectors ›
  // Outreach row (`alfredInSymphonyOutreach`; the rule is `listedOnTools` beside each client's Outreach
  // list). A server-side hide would reach app builds that have no Outreach row yet.
};

/**
 * What the Connectors › Outreach tab's Alfred row asks the assistant when a connected user presses Manage —
 * the same "what's next" steps the `using-alfred` skill offers right after setup (customize the
 * receptionist, forward the business line to it). English on purpose, like `connectPrompt`: it is addressed
 * to the assistant, which answers in the user's language. The phones carry the identical literal (iOS
 * `OutreachAlfred.alfredManagePrompt`, Android `ALFRED_MANAGE_PROMPT`).
 */
export const ALFRED_MANAGE_PROMPT = 'I want to customize Alfred or set up call forwarding.';

/**
 * What the onboarding's last step ("Alfred is ready") asks the assistant when the user presses Customize in
 * chat — the skill's "Customize" option, which reads how Alfred sounds now and offers to change its name,
 * tone, voice or greeting. English on purpose, like `ALFRED_MANAGE_PROMPT`; the phones carry the identical
 * literal (iOS `AlfredSetup.customizePrompt`, Android `ALFRED_CUSTOMIZE_PROMPT`).
 */
export const ALFRED_CUSTOMIZE_PROMPT = 'I want to customize how Alfred sounds.';

/**
 * Link's mark, hosted like GBP's and Alfred's: `logos.composio.dev` has none and answers `link` /
 * `link-wallet` with the generic placeholder and a 200 (checked 2026-09-27). The asset is NOT uploaded yet
 * — the same position as `PLAUD_LOGO_URL`, safe while the rollout is an allowlist.
 */
export const LINK_WALLET_LOGO_URL = 'https://symphony.wix.com/link-wallet.svg';

/**
 * Paired with the `link-wallet-rollout` databag value. A rollout is TWO applies with the same value: on
 * orion-harness-poc (the harness offer and connect by name) and on ai-scheduling-assistant-server (the
 * Connectors screen, and mcp-connection's `CreateConnectorConnection` gate, which reads it cross-artifact).
 * The harness reads its own copy because its cross-artifact read of the server's key did not deliver (#8230).
 * Off is `none`, not blank: Dev Portal refuses an empty value, and `none` matches no metasite id.
 */
export const LINK_WALLET_ROLLOUT_KEY = 'link-wallet';

/**
 * Link, Stripe's wallet: a registered connector (mcp-connection's `LINK_WALLET`, slug `link-wallet`).
 *
 * `link-wallet-rollout` is `true` (open to every account) since 2026-10-08. mcp-connection enforces the geo rule
 * (US, Canada, Mexico) at connect time. The harness offers a NEW Link connect only where `ListAvailableConnectors`
 * (rollout + geo) lists it, so the chat never offers a connect the server would refuse; a Link already connected
 * stays offered wherever the user is (`link-wallet-offer.ts`), since that list leaves connected connectors out.
 * Both Connectors screens apply the same read (ai-scheduling-assistant-server `services/linkWalletRow.ts`), and keep
 * the row of any existing Link connection so it can always be reconnected or removed.
 *
 * Never `gated: true`: the shared `first-party-connector-rollout` is already open to everyone.
 */
export const LINK_WALLET_CONNECTOR = {
  kind: 'registered-connector',
  slug: 'link-wallet',
  // Not the bare "Link": the name is also a resolvable spelling, and "link" is an ordinary word ("send me the
  // link", LinkedIn), so it would match prose and requests that mean something else.
  name: 'Link by Stripe',
  logoUrl: LINK_WALLET_LOGO_URL,
  description: 'Pay for a purchase Symphony prepares with a card saved in Link by Stripe, after you approve it in Link.',
  // A noun clause, like the other prerequisites: surfaces splice it into "Needs …". The geo part mirrors the
  // server's `GeoPolicy.includeOnly("US", "CA", "MX")`, judged on the request's own geo, else the account country
  // (`payment-country.ts`). It leaves that judgement to the connect because "only for US users" read as a fact
  // the assistant could settle: it refused a user on a US connection because their street address was in
  // Israel, and never tried the connect.
  prerequisite: 'a Link (link.com) account with a saved card, and a connection made from the US, Canada or Mexico, which only the connect itself can settle: never judge it from an address or anything else you hold for them',
  // A merchant asked to "accept payments from link cli agents" and was handed the connect card (2026-10-10):
  // the name matched, and nothing on the row said Link only ever spends.
  notFor: "It spends the user's own card and never receives money. A user who wants to accept or collect payments, from customers or from AI agents that pay with Link, gets nothing from connecting it, so never offer or connect it for that: an agent paying with Link pays with a one-time card, through the card checkout of the user's own payment provider.",
  connectPrompt: 'I want to connect Link by Stripe.',
  // `link wallet` is the name it had before, still how some users and old chat rows ask for it.
  aliases: ['link wallet', 'link.com', 'stripe link'],
  rolloutKey: LINK_WALLET_ROLLOUT_KEY
};
export const GOOGLE_APPS_SCRIPT_CONNECTOR = {
  kind: 'registered-connector',
  slug: 'google-apps-script',
  name: 'Google Apps Script',
  logoUrl: 'https://www.gstatic.com/images/branding/product/1x/apps_script_48dp.png',
  description: 'Create, edit, deploy and run your Apps Script projects under your Google account. Each change and execution requires owner approval.',
  prerequisite: 'a Google account and a customer-owned Google Cloud project with a web OAuth client; the setup page lists the steps to create your own Cloud project and OAuth client, then authorizes your account',
  notFor: 'V1 supports Sheets and Drive scripts; recurring triggers and other Google services are not supported.',
  skill: 'using-google-apps-script',
  connectPrompt: 'I want to connect Google Apps Script.',
  aliases: ['apps script', 'appscript', 'google app script'],
  rolloutKey: 'google-apps-script'
};
export const FIRST_PARTY_MCP_CONNECTORS = [WORDPRESS_MCP_CONNECTOR, TIKTOK_CONNECTOR, SHOPIFY_CONNECTOR, SHOPIFY_DIRECT_CONNECTOR, GBP_CONNECTOR, PLAUD_CONNECTOR, ALFRED_CONNECTOR, LINK_WALLET_CONNECTOR, GSC_CONNECTOR, SOCIAL_FACEBOOK_CONNECTOR, BING_WEBMASTER_CONNECTOR, GOOGLE_APPS_SCRIPT_CONNECTOR];

/**
 * The registry as THIS caller should see it: the ungated entries, plus the gated ones when the rollout is
 * open for this caller (`ConfigProvider.isFirstPartyConnectorEnabledFor`).
 *
 * Every surface that enumerates or resolves a connector goes through this, so a rollout is one decision
 * rather than one per surface. Pure, and takes the verdict rather than reading config itself: the two
 * services hold different config providers, and a registry that could read anything is one that could
 * fail open.
 */
export function firstPartyConnectorsFor(rolloutOpen,
/**
 * Verdicts for entries on their OWN rollout key (see `rolloutKey`), by key. Defaults to none, so an
 * entry with its own key stays hidden for any caller that has not been taught about it — the same
 * fail-closed direction as `rolloutOpen` defaulting to the ungated set.
 */
ownRollouts) {
  if (ownRollouts === void 0) {
    ownRollouts = {};
  }
  return FIRST_PARTY_MCP_CONNECTORS.filter(connector => connector.rolloutKey ? ownRollouts[connector.rolloutKey] === true : !connector.gated || rolloutOpen);
}

/** True when this entry is behind SOME rollout, whichever kind — for callers that only need to know
 *  whether an entry can be dark at all (e.g. the reconnect carve-out for a connector the user owns). */
export function isGatedConnector(connector) {
  return connector.gated === true || connector.rolloutKey !== undefined;
}
const normalizeMcpUrl = url => url.trim().toLowerCase().replace(/\/+$/, '');

/**
 * Take our metasite back off an install link, so a link we HANDED OUT still matches the entry it came
 * from. `attributedInstallUrl` is the inverse.
 *
 * Needed because the registry holds the canonical `utm_source=symphony` while every user sees
 * `utm_source=symphony-{their msid}`. Without this, the address the assistant is most likely to pass back
 * is the one address the partner-install lookup fails to recognise — and the caller's next step for an
 * unrecognised http(s) value is to probe it as an MCP server.
 *
 * Strips only a trailing GUID, so it cannot turn some other partner's attribution into ours.
 */
const STAMPED_ATTRIBUTION = new RegExp(`(${ATTRIBUTION_PARAM}=[^&#]*?)-${GUID_PATTERN}(?=$|[&#])`, 'i');
const unstampInstallUrl = url => url.replace(STAMPED_ATTRIBUTION, '$1');

/**
 * Match a live MCP connection back to its registry entry by URL (trailing-slash/case tolerant).
 *
 * Only `mcp-server` entries can be found this way, because only they HAVE a URL. A registered
 * connector's row stores whatever the vendor's API base was when it was written, and the address the
 * assistant dials is derived server-side — so there is no URL that identifies it. Use
 * `findFirstPartyMcpForConnection` when you hold a connection and want its entry whatever the rail.
 */
export function findFirstPartyMcpByUrl(url, /** The entries this caller's rollout allows; defaults to the whole registry for ungated callers. */
entries) {
  if (entries === void 0) {
    entries = FIRST_PARTY_MCP_CONNECTORS;
  }
  if (!url) {
    return undefined;
  }
  const needle = normalizeMcpUrl(url);
  return entries.find(connector => connector.kind === 'mcp-server' && normalizeMcpUrl(connector.url) === needle);
}

/**
 * The partner-install entry whose `installUrl` this address is, searched across the WHOLE registry
 * regardless of rollout.
 *
 * This exists to CLOSE a hole rather than to open a path. `findFirstPartyMcpByUrl` deliberately cannot
 * return a partner install, so an install link falls through every registry lookup — and a connect path
 * that ends in "any http(s) value is a server address" then treats a marketplace listing as an MCP server
 * and probes it. Callers ask this before that fallback and refuse.
 *
 * Rollout-blind on purpose: the answer to "is this address a marketplace listing?" is a fact about the
 * address, and an off-rollout caller must still be refused rather than dropped into the probe.
 */
export function findPartnerInstallByInstallUrl(url) {
  if (!url) {
    return undefined;
  }
  // Normalize FIRST: the id sits at the very end of the address, so a copied link's trailing slash would
  // otherwise sit between the id and the end of the string and stop it being recognised as stamped.
  const needle = unstampInstallUrl(normalizeMcpUrl(url));
  return FIRST_PARTY_MCP_CONNECTORS.find(connector => connector.kind === 'partner-install' && normalizeMcpUrl(connector.installUrl) === needle);
}

/** The shape this needs off a live connection — structural, so callers needn't import the entity. */

/**
 * The registry entry a live connection belongs to, on either rail: by `connector_slug` when the service
 * set one, else by URL.
 *
 * Slug is tried FIRST and is the only thing a registered connector is matched on. Its `url` column holds
 * the vendor API base a pre-gateway connect wrote (e.g. `https://open.tiktokapis.com`) and nothing
 * rewrites it on read — the gateway URL is derived in `GetFreshCredentials` only, so `Query` still
 * returns the stored value. Matching those rows by URL would silently fail to pair a live connection with
 * its own row, which renders a connected integration as "Connect" and offers a second connection over the
 * one the user already has.
 */
export function findFirstPartyMcpForConnection(connection) {
  var _connection$connector2;
  const bySlug = (_connection$connector2 = connection.connectorSlug) == null ? void 0 : _connection$connector2.trim();
  if (bySlug) {
    const needle = normalizeMcpSlug(bySlug);
    // Restricted to the connection-backed rails on purpose. A `wix-service` entry can never be behind an
    // McpConnection, so letting one match here would pair a live row with an entry whose authorize and
    // disconnect go somewhere else entirely.
    return FIRST_PARTY_MCP_CONNECTORS.filter(isMcpBackedConnector).find(connector => normalizeMcpSlug(connector.slug) === needle);
  }
  return findFirstPartyMcpByUrl(connection.url);
}

/** Every spelling of a slug or display name collapses to one key, so `WordPress` / `word_press` /
 *  `word-press` all resolve. Matches how the harness and the server normalize a Composio toolkit,
 *  which is what keeps a registry entry blocked on the Composio path under any spelling. */
const normalizeMcpSlug = slug => slug.toLowerCase().replace(/[^a-z0-9]/g, '');

/** Every name a connector answers to: its slug, its display name, and its declared aliases, all collapsed
 *  to comparison keys. Exported because the harness blocks the Composio path for these same spellings, and
 *  a spelling only one of the two sides knows is exactly how a connector becomes unreachable. */
export function firstPartyMcpNorms(connector) {
  return [connector.slug, connector.name, ...(connector.aliases ?? [])].map(normalizeMcpSlug);
}

/**
 * True when this entry retires the Composio toolkit of the same name, so every spelling of it belongs on the
 * Composio block lists. The harness and the server both build theirs from the registry through this filter,
 * so an entry still piloting its replacement (`liveComposioToolkit`) stays off both at once.
 */
export function blocksComposioToolkit(connector) {
  return !connector.liveComposioToolkit;
}

/**
 * The Composio spellings retired FOR ONE CALLER: every entry of `entries` (the caller's rollout view, see
 * `firstPartyConnectorsFor`) that keeps its Composio twin live (`liveComposioToolkit`), plus that twin's own
 * slug. `blocksComposioToolkit` answers for every account at once; this answers for the account whose rollout
 * already shows the replacement, so it sees ONE Facebook rather than the tile and the Composio
 * row side by side. Empty for a caller off the rollout, whose Composio toolkit stays untouched.
 *
 * Deleted together with `liveComposioToolkit`: once the flip sets such an entry's rollout to `true`, the static
 * block lists cover everyone and nothing is left to decide per caller.
 */
export function retiredComposioNormsFor(entries) {
  return new Set(entries.flatMap(connector => connector.liveComposioToolkit ? [...firstPartyMcpNorms(connector), normalizeMcpSlug(connector.liveComposioToolkit)] : []));
}

/**
 * The entry piloting a replacement for this Composio toolkit (`liveComposioToolkit`), when that is what the spelling
 * names. Deliberately apart from `findFirstPartyMcpBySlug`: the Composio slug is not one of the entry's own spellings
 * (Composio Facebook's tools are `FACEBOOK_*`, so making it one would retire them for every account), yet a CONNECT
 * for it must still reach the entry, whose rollout decides between the new rail and the live Composio toolkit. Read it
 * on connect paths only, after `findFirstPartyMcpBySlug` misses.
 *
 * Deleted together with `liveComposioToolkit`.
 */
export function findFirstPartyMcpPilotingComposioToolkit(slug, entries) {
  if (entries === void 0) {
    entries = FIRST_PARTY_MCP_CONNECTORS;
  }
  if (!slug) {
    return undefined;
  }
  const needle = normalizeMcpSlug(slug);
  return entries.find(connector => connector.liveComposioToolkit !== undefined && normalizeMcpSlug(connector.liveComposioToolkit) === needle);
}

/**
 * Resolve a spelling to its entry. `entries` narrows the search to what the caller's rollout allows — pass
 * `firstPartyConnectorsFor(...)` on any path that CONNECTS, or a dark-launched connector stays connectable
 * by name for anyone who says it, which is the rollout leaking through the back door.
 */
export function findFirstPartyMcpBySlug(slug, entries) {
  if (entries === void 0) {
    entries = FIRST_PARTY_MCP_CONNECTORS;
  }
  if (!slug) {
    return undefined;
  }
  const needle = normalizeMcpSlug(slug);
  return entries.find(connector => firstPartyMcpNorms(connector).includes(needle));
}

/**
 * Connectors that COMPETE with a Wix-family product and are therefore never proactively OFFERED, on any
 * surface: not in the assistant's connector menu, not on the Connectors screen's connectable list, not as
 * a dashboard Connect card, not in the ready-made MCP listing. Wix is the website/commerce surface and
 * Wixel is the content/design surface, and the platform prefers its own. A deprioritized connector still
 * CONNECTS when the user actively asks for it by name, and an already-connected one keeps its row so it
 * stays manageable — this hides the sales pitch, never the user's own connection.
 */
// Shopify came OFF this list once the Shopify-API-Terms question about Rise's credentials serving
// Symphony was settled (raised 2026-08-20, closed since). It is a Wix-family competitor on the surface,
// but Symphony reaches it through Rise — a Wix product — so connecting one is a Wix store owner keeping
// a second storefront, not a customer leaving. The rest stay request-only.
const DEPRIORITIZED_CONNECTOR_NORMS = new Set(['wordpress', 'wordpresscom', 'wordpressorg', 'canva']);

/** True when a connector slug/name is a Wix-family competitor: connectable on request, never suggested. */
export function isDeprioritizedConnectorSlug(slugOrName) {
  return DEPRIORITIZED_CONNECTOR_NORMS.has(normalizeMcpSlug(slugOrName));
}
/**
 * Composio has no per-site switch: one enabled auth config reaches every site, and disabling it drops every
 * connection. So a pilot toolkit is gated here, and refused on connect as well as hidden from listings.
 */
const ROLLOUT_GATED_COMPOSIO_NORMS = {
  zohomail: 'zohoMail',
  // Composio's Zoho CRM toolkit is the bare `zoho`; `zohocrm` is how people name it.
  zoho: 'zohoCrm',
  zohocrm: 'zohoCrm',
  jobber: 'jobber',
  xero: 'xero',
  // Composio's X toolkit keeps its old name, `twitter`.
  twitter: 'twitter',
  x: 'twitter'
};
export function isComposioToolkitAllowedFor(slugOrName, rollouts) {
  const key = ROLLOUT_GATED_COMPOSIO_NORMS[normalizeMcpSlug(slugOrName)];
  return key === undefined || rollouts[key] === true;
}

/**
 * Toolkits limited to one connected account per site. A Draft Jobber app connects at most 5 paying Jobber
 * accounts, and the rollout list above caps sites, not accounts, so a second account on a pilot site could
 * be the sixth, which gets the app blocked for everyone.
 */
const SINGLE_ACCOUNT_COMPOSIO_NORMS = new Set(['jobber']);
export function allowsMultipleComposioAccounts(slugOrName) {
  return !SINGLE_ACCOUNT_COMPOSIO_NORMS.has(normalizeMcpSlug(slugOrName));
}
const PENDING_CONNECT_LINK_STATUSES = new Set(['INITIATED', 'INITIALIZING']);

/** A connected-account record whose authorize link has been minted and not yet completed. */
export function isPendingConnectLink(status) {
  return PENDING_CONNECT_LINK_STATUSES.has(status ?? '');
}

/**
 * What a post-authorization redirect proves about its connect attempt, from the state Composio holds for the
 * account the link created. ACTIVE connected. FAILED failed. A link still waiting has not connected, whatever
 * the URL says, but a denial leaves it waiting too, so a redirect saying it failed is believed for the
 * failure alone: the worst a forged one can do is record a failure a real success later overrides. Anything
 * else (EXPIRED, INACTIVE, no account) is not the redirect's doing, and proves nothing.
 */
export function connectOutcomeOfAccountState(state, redirectStatus) {
  if (state === 'ACTIVE') {
    return 'success';
  }
  if (state === 'FAILED' || redirectStatus === 'failed' && isPendingConnectLink(state)) {
    return 'failed';
  }
  return undefined;
}
const CONNECT_ACCOUNT_REREAD_PAUSE_MILLIS = 400;

/**
 * The state `read` reports for the account a connect link created, read again while it cannot settle the
 * redirect yet: while the read fails, or while the account is still waiting under a redirect that says it
 * succeeded (Composio can redirect a moment before it marks the account ACTIVE). Gives up at `budgetMillis`
 * with the last answer it had, so a slow Composio costs the landing its budget and nothing more. `answered`
 * is false when no read answered; `reads` counts the reads started, settled or not.
 */
export async function readSettledConnectAccountState(read, redirectStatus, budgetMillis) {
  const deadline = Date.now() + budgetMillis;
  let reads = 0;
  let answer = {
    answered: false
  };
  while (Date.now() < deadline) {
    let timer;
    try {
      const outOfTime = new Promise(resolve => {
        timer = setTimeout(() => resolve('out-of-time'), deadline - Date.now());
      });
      reads += 1;
      const state = await Promise.race([read(), outOfTime]);
      if (state === 'out-of-time') {
        break;
      }
      answer = {
        answered: true,
        state
      };
      if (!(redirectStatus === 'success' && isPendingConnectLink(state))) {
        break;
      }
    } catch {
      // A failed read is retried like a waiting account: the next one may answer.
    } finally {
      clearTimeout(timer);
    }
    const pause = Math.min(CONNECT_ACCOUNT_REREAD_PAUSE_MILLIS, deadline - Date.now());
    if (pause > 0) {
      await new Promise(resolve => setTimeout(resolve, pause));
    }
  }
  return {
    ...answer,
    reads
  };
}
/**
 * A delete that answered 404: the record is already gone, which is the outcome the delete wanted. Two racing
 * mints both delete the losing link, so one of them always sees this, and counting it as a failure would make
 * the winner drop its own link too.
 */
export function isAlreadyDeleted(error) {
  return (error == null ? void 0 : error.status) === 404;
}
const linkTime = record => Date.parse(record.createdAt ?? '') || 0;

/**
 * The ids of every pending link except the newest. Two single-account mints that race each run this right
 * after minting, so both keep the same survivor and the other link is dead before anyone can open it.
 */
export function olderPendingLinkIds(records) {
  return records.filter(record => isPendingConnectLink(record.status)).sort((a, b) => linkTime(b) - linkTime(a) || b.id.localeCompare(a.id)).slice(1).map(record => record.id);
}

/**
 * What to do right after minting a link for a single-account toolkit. Our link stands only while it is the
 * newest pending link and no other record is ACTIVE; then the older pending links go. Otherwise ours goes.
 */
export function singleAccountMintVerdict(records, ownId) {
  const older = olderPendingLinkIds(records);
  const keepOwn = !older.includes(ownId) && !records.some(record => record.status === 'ACTIVE' && record.id !== ownId);
  return keepOwn ? {
    keepOwn,
    deleteIds: older
  } : {
    keepOwn,
    deleteIds: [ownId]
  };
}